import { classes } from '@theme/constants';
import { publish } from '@theme/event-bus';
import { gsap } from '@theme/gsap';
import { HTMLShopifySectionElement } from '@theme/html-custom-element';

class LoadingScreen extends HTMLShopifySectionElement {
  type = 'logo';

  async connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (isLoadingScreenShown()) {
      publish('loading-screen-closed');
      return;
    }

    try {
      await this.refs.video.play().then(() => {
        this.type = 'video';
      });
    } catch {
      /* empty */
    }

    if (this.type === 'video') {
      this.refs.video.classList.toggle(classes.hidden, this.type === 'logo');
    }

    this.refs.logo.classList.toggle(classes.hidden, this.type === 'video');
    this.refs.brandmark.classList.toggle(classes.hidden, this.type === 'video');

    /**
     * Lock the body scroll.
     */
    document.body.style.overflow = 'hidden';

    this.timeline = this.createTimeline().add(
      /**
       * Fade out when complete.
       */
      gsap.to(this, {
        opacity: 0,
        pointerEvents: 'none',
        delay: this.type === 'video' ? 5 : 1,
        duration: 0.2,
        onComplete() {
          publish('loading-screen-closed');

          /**
           * Release the body scroll lock.
           */
          document.body.style.overflow = '';

          /**
           * Stores the shown state.
           */
          localStorage.setItem('loading-screen-shown', true);
        },
      }),
    );
  }

  /**
   * Creates a timeline instance.
   * @returns {ReturnType<typeof import('@theme/gsap').timeline>}
   */
  createTimeline() {
    const timeline = gsap.timeline();

    if (this.type === 'video') {
      return timeline;
    }

    if (this.refs.logo)
      timeline.add(
        gsap.to(this.refs.logo, {
          opacity: 1,
          yPercent: -50,
          ease: 'power1.out',
          delay: 0.5,
        }),
      );

    if (this.refs.tagline)
      timeline.add(
        gsap.to(
          this.refs.tagline,
          {
            opacity: 1,
          },
          '<',
        ),
      );

    if (this.refs.logo)
      timeline.add(
        gsap.to(this.refs.logo, {
          opacity: 0,
          delay: 0.75,
          yPercent: -100,
        }),
      );

    if (this.refs.brandmark)
      timeline.add(
        gsap.to(
          this.refs.brandmark,
          {
            opacity: 1,
            yPercent: -50,
          },
          '<=',
        ),
      );

    return timeline;
  }

  onSectionSelect(event) {
    if (event.detail.load) return;
    if (this.timeline) this.timeline.kill();
    if (this.editorTimeline) this.editorTimeline.kill();

    gsap.to(this, {
      opacity: 1,
      pointerEvents: 'auto',
      duration: 0.2,
    });

    if (!this.editorTimeline) {
      this.editorTimeline = this.createTimeline();
      return;
    }

    this.editorTimeline.restart();
  }

  onSectionDeselect() {
    if (this.editorTimeline) this.editorTimeline.kill();

    gsap.to(this, {
      opacity: 0,
      pointerEvents: 'none',
      duration: 0.2,
    });
  }
}

/**
 * Returns if the loading screen has been shown.
 * @returns {boolean}
 */
export function isLoadingScreenShown() {
  return !document.querySelector('loading-screen') || localStorage.getItem('loading-screen-shown') === 'true';
}

if (!customElements.get('loading-screen')) customElements.define('loading-screen', LoadingScreen);
